import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import "./Layout.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [logoError, setLogoError] = useState(false);
  const [headerLogoError, setHeaderLogoError] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [pendingPTOCount, setPendingPTOCount] = useState(0);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showEditPhone, setShowEditPhone] = useState(false);
  const [phoneForm, setPhoneForm] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneSuccess, setPhoneSuccess] = useState("");
  const [phoneLoading, setPhoneLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();

  const handleEditPhone = async (e) => {
    e.preventDefault();
    setPhoneError("");
    setPhoneSuccess("");

    if (phoneForm && !/^\+1[2-9]\d{9}$/.test(phoneForm)) {
      setPhoneError("Phone number must be in E.164 format (e.g. +12125551234)");
      return;
    }

    setPhoneLoading(true);
    try {
      await axios.put(`${API_URL}/auth/profile`, {
        phoneNumber: phoneForm || "",
      });
      setPhoneSuccess("Phone number updated successfully!");
      setTimeout(() => {
        setShowEditPhone(false);
        setPhoneSuccess("");
        window.location.reload();
      }, 1500);
    } catch (error) {
      setPhoneError(
        error.response?.data?.message || "Failed to update phone number",
      );
    }
    setPhoneLoading(false);
  };

  // Get user initials from name
  const getUserInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (passwordForm.newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.post(`${API_URL}/auth/change-password`, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordSuccess("Password changed successfully!");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => {
        setShowChangePassword(false);
        setPasswordSuccess("");
      }, 2000);
    } catch (error) {
      setPasswordError(
        error.response?.data?.message || "Failed to change password",
      );
    }
    setPasswordLoading(false);
  };

  const isActive = (path) => {
    if (
      path === "/" &&
      (location.pathname === "/" || location.pathname === "/reports")
    ) {
      return true;
    }
    return location.pathname === path;
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest(".user-avatar-container")) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserMenu]);

  // // Fetch pending PTO count for admins
  // useEffect(() => {
  //   const fetchPendingCount = async () => {
  //     if (isAdmin) {
  //       try {
  //         const response = await axios.get(`${API_URL}/pto/pending/count`);
  //         setPendingPTOCount(response.data.count);
  //       } catch (error) {
  //         console.error('Error fetching pending PTO count:', error);
  //       }
  //     }
  //   };

  //   fetchPendingCount();
  //   // Refresh count every 30 seconds
  //   const interval = setInterval(fetchPendingCount, 30000);
  //   return () => clearInterval(interval);
  // }, [isAdmin]);

  const handlePendingNotificationClick = () => {
    navigate("/admin");
    // Scroll to PTO management section after navigation
    setTimeout(() => {
      const ptoSection = document.querySelector(".pto-management-section");
      if (ptoSection) {
        ptoSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  return (
    <div className="layout">
      <div className={`sidebar ${sidebarOpen ? "open" : "closed"}`}>
        <div className="sidebar-header">
          <div className="logo">
            {logoError ? (
              <span className="logo-text">TeaTime</span>
            ) : (
              <img
                src="/logo.svg"
                alt="TeaTime"
                className="logo-image"
                onError={() => setLogoError(true)}
              />
            )}
          </div>
        </div>
        <nav className="sidebar-nav">
          <Link to="/" className={`nav-item ${isActive("/") ? "active" : ""}`}>
            <span className="nav-icon">📊</span>
            <span className="nav-text">Dashboard</span>
          </Link>
          {!isAdmin && (
            <Link
              to="/upload"
              className={`nav-item ${isActive("/upload") ? "active" : ""}`}
            >
              <span className="nav-icon">☁️</span>
              <span className="nav-text">Upload</span>
            </Link>
          )}
          {isAdmin && (
            <>
              <Link
                to="/admin"
                className={`nav-item ${isActive("/admin") ? "active" : ""}`}
              >
                <span className="nav-icon">🏠</span>
                <span className="nav-text">Admin</span>
              </Link>
              <Link
                to="/users"
                className={`nav-item ${isActive("/users") ? "active" : ""}`}
              >
                <span className="nav-icon">👥</span>
                <span className="nav-text">Users</span>
              </Link>
            </>
          )}
          <Link
            to="/history"
            className={`nav-item ${isActive("/history") ? "active" : ""}`}
          >
            <span className="nav-icon">🕐</span>
            <span className="nav-text">History</span>
          </Link>
          <Link
            to="/holidays"
            className={`nav-item ${isActive("/holidays") ? "active" : ""}`}
          >
            <span className="nav-icon">🎉</span>
            <span className="nav-text">Holidays</span>
          </Link>
        </nav>
        <button
          className="sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? "←" : "→"}
        </button>
      </div>
      <div className="main-content">
        <header className="header">
          <div className="header-left">
            <div className="logo-small">
              {headerLogoError ? (
                <span className="logo-text-small">TeaTime</span>
              ) : (
                <img
                  src="/logo.svg"
                  alt="TeaTime"
                  className="logo-image-small"
                  onError={() => setHeaderLogoError(true)}
                />
              )}
            </div>
          </div>
          <div className="header-right">
            {isAdmin && pendingPTOCount > 0 && (
              <div
                className="notification-badge-container"
                title={`${pendingPTOCount} pending PTO request${pendingPTOCount !== 1 ? "s" : ""}`}
                onClick={handlePendingNotificationClick}
              >
                <span className="notification-icon">🔔</span>
                <span className="notification-count">{pendingPTOCount}</span>
                <span className="notification-text">Pending PTO Requests</span>
              </div>
            )}
            <div className="user-avatar-container">
              <div
                className="user-avatar"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                {getUserInitials(user?.name || "User")}
              </div>
              {showUserMenu && (
                <div className="user-menu">
                  <div className="user-menu-header">
                    <div className="user-menu-avatar">
                      {getUserInitials(user?.name || "User")}
                    </div>
                    <div className="user-menu-info">
                      <div className="user-menu-name">
                        {user?.name || "User"}
                      </div>
                      <div className="user-menu-email">{user?.email || ""}</div>
                    </div>
                  </div>
                  <div className="user-menu-divider"></div>
                  <div
                    className="user-menu-item"
                    onClick={() => {
                      setShowChangePassword(true);
                      setShowUserMenu(false);
                    }}
                  >
                    <span className="user-menu-icon">🔒</span>
                    <span>Change Password</span>
                  </div>
                  <div
                    className="user-menu-item"
                    onClick={() => {
                      setPhoneForm(user?.phoneNumber || "");
                      setShowEditPhone(true);
                      setShowUserMenu(false);
                    }}
                  >
                    <span className="user-menu-icon">📱</span>
                    <span>Edit Phone Number</span>
                  </div>
                  <div className="user-menu-item" onClick={handleLogout}>
                    <span className="user-menu-icon">→</span>
                    <span>Log Out</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="content">{children}</main>

        {showChangePassword && (
          <div
            className="modal-overlay"
            onClick={() => {
              setShowChangePassword(false);
              setPasswordError("");
              setPasswordSuccess("");
            }}
          >
            <div
              className="change-password-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="change-password-header">
                <h3>Change Password</h3>
                <button
                  className="modal-close-btn"
                  onClick={() => {
                    setShowChangePassword(false);
                    setPasswordError("");
                    setPasswordSuccess("");
                  }}
                >
                  ✕
                </button>
              </div>
              <form
                onSubmit={handleChangePassword}
                className="change-password-form"
              >
                {passwordError && (
                  <div className="change-password-error">{passwordError}</div>
                )}
                {passwordSuccess && (
                  <div className="change-password-success">
                    {passwordSuccess}
                  </div>
                )}
                <div className="change-password-field">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        currentPassword: e.target.value,
                      })
                    }
                    required
                    placeholder="Enter current password"
                  />
                </div>
                <div className="change-password-field">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        newPassword: e.target.value,
                      })
                    }
                    required
                    placeholder="Enter new password"
                  />
                </div>
                <div className="change-password-field">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        confirmPassword: e.target.value,
                      })
                    }
                    required
                    placeholder="Confirm new password"
                  />
                </div>
                <button
                  type="submit"
                  className="change-password-btn"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? "Changing..." : "Change Password"}
                </button>
              </form>
            </div>
          </div>
        )}

        {showEditPhone && (
          <div
            className="modal-overlay"
            onClick={() => {
              setShowEditPhone(false);
              setPhoneError("");
              setPhoneSuccess("");
            }}
          >
            <div
              className="change-password-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="change-password-header">
                <h3>Edit Phone Number</h3>
                <button
                  className="modal-close-btn"
                  onClick={() => {
                    setShowEditPhone(false);
                    setPhoneError("");
                    setPhoneSuccess("");
                  }}
                >
                  ✕
                </button>
              </div>
              <form onSubmit={handleEditPhone} className="change-password-form">
                {phoneError && (
                  <div className="change-password-error">{phoneError}</div>
                )}
                {phoneSuccess && (
                  <div className="change-password-success">{phoneSuccess}</div>
                )}
                <div className="change-password-field">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={phoneForm}
                    onChange={(e) => setPhoneForm(e.target.value)}
                    placeholder="+1XXXXXXXXXX"
                  />
                  <small
                    style={{
                      color: "#666",
                      fontSize: "12px",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    US/Canada format: +1 followed by 10 digits (e.g.
                    +12125551234). Leave blank to clear.
                  </small>
                </div>
                <button
                  type="submit"
                  className="change-password-btn"
                  disabled={phoneLoading}
                >
                  {phoneLoading ? "Saving..." : "Save Phone Number"}
                </button>
              </form>
            </div>
          </div>
        )}
        <footer className="footer">
          <div className="footer-left">
            <p>© 2026 TeaTime Dashboard. All rights reserved.</p>
            <p>Made with ❤️</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
